import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { ClientOnly } from '~/design-system/components/ClientOnly/ClientOnly';
import type { BirtvyDemoHandle } from '~/design-system/components/DeckGen/birtvy/BirtvyCanvas';
import styles from './HeroWindowDemo.module.css';

/**
 * What runs inside the hero window's body: the real Birtvy editor, holding one real deck per file.
 *
 * Each deck is a static JSON under public/landing/hero-decks, exported once from the product itself
 * (see that folder's README). Fetched per file rather than bundled, so the landing ships none of it
 * until you actually open that tab, and swapping a deck is replacing a file.
 *
 * The canvas is client-only and lazy: it is the heaviest module in the app and the landing page is
 * the one route that has to stay light, so it never joins the server render or the first bundle.
 */

const BirtvyCanvas = lazy(() =>
  import('~/design-system/components/DeckGen/birtvy/BirtvyCanvas').then((m) => ({
    default: m.BirtvyCanvas,
  })),
);

/** One entry per tab in the title bar. `null` = nothing exported for that file yet. */
const DECK_SOURCES: (string | null)[] = [
  '/landing/hero-decks/1-pitch-deck.json',
  null,
  null,
  null,
  null,
];

interface HeroDeckDoc {
  id: number;
  title: string;
  content: string;
  x: number;
  y: number;
  w?: number;
  h?: number;
}

interface HeroDeckFile {
  title: string;
  docs?: HeroDeckDoc[];
}

export interface HeroWindowDemoProps {
  /** Index of the open deck file, matching the order of the tabs in the title bar. */
  index: number;
}

export function HeroWindowDemo({ index }: HeroWindowDemoProps) {
  const handleRef = useRef<BirtvyDemoHandle | null>(null);
  const src = DECK_SOURCES[index] ?? null;

  const demo = useMemo(
    () => ({
      // `demo` marks this as the landing's canvas: no persistence, no load screen, no account chrome,
      // and a plain wheel left to the page (pinch still zooms).
      onReady: (handle: BirtvyDemoHandle) => {
        handleRef.current = handle;
      },
    }),
    [],
  );

  useEffect(() => {
    if (!src) return;
    let cancelled = false;

    // The canvas hands its control surface over on mount, which can land after this effect. Poll
    // briefly rather than racing it — a missed handle means an empty window with no way to recover.
    const place = async () => {
      const res = await fetch(src);
      if (!res.ok || cancelled) return;
      const deck = (await res.json()) as HeroDeckFile;

      for (let i = 0; i < 40 && !handleRef.current && !cancelled; i += 1) {
        await new Promise((r) => setTimeout(r, 100));
      }
      const c = handleRef.current;
      if (!c || cancelled) return;

      c.reset();
      const docs = deck.docs ?? [];
      docs.forEach((d) => c.addDoc({ title: d.title, content: d.content }, { x: d.x, y: d.y }));

      // Frame everything that was just placed, so the file opens showing its own contents rather
      // than wherever the camera happened to rest.
      if (docs.length) {
        const pad = 400;
        const DOC_W = 764;
        const DOC_H = 1080;
        const minX = Math.min(...docs.map((d) => d.x)) - pad;
        const minY = Math.min(...docs.map((d) => d.y)) - pad;
        const maxX = Math.max(...docs.map((d) => d.x + (d.w ?? DOC_W))) + pad;
        const maxY = Math.max(...docs.map((d) => d.y + (d.h ?? DOC_H))) + pad;
        c.frameRect({ x: minX, y: minY, w: maxX - minX, h: maxY - minY }, { animate: false });
      }
    };

    void place();
    return () => {
      cancelled = true;
    };
  }, [src, index]);

  return (
    <div className={styles.body} data-name="HeroWindowDemo">
      <ClientOnly fallback={<div className={styles.fallback} aria-hidden="true" />}>
        {/* Keyed on the file so switching gives a fresh board rather than the last one's leftovers. */}
        <div key={index} className={styles.mount}>
          <Suspense fallback={<div className={styles.fallback} aria-hidden="true" />}>
            <BirtvyCanvas demo={demo} />
          </Suspense>
        </div>
      </ClientOnly>
    </div>
  );
}

HeroWindowDemo.displayName = 'HeroWindowDemo';
